import type { ChartData } from 'chart.js';
import { useDeepCompareEffect } from 'react-use';

import type { DateFilterState } from '@/features/Commons/DatePresetFilter';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import { getTimezoneDayjs, parseDateFormatByRangeUnit } from '@/utils/time';
import { highlight, removeHighlight } from '@/utils/util';

type Props = {
  selectedChartBar: SelectedChartBar;
  dateState: DateFilterState;
  trendChartData: ChartData<'bar'>;
  searchValue: string;
  sortStatus: SortStatusT;
  filterQueryFormats: StructuredFilterQueryFormatT[];
  clearCheckList: () => void;
};

const useFetchFeedbackList = (props: Props) => {
  const fetchFeedbackList = useFeedbackListStore(
    (state) => state.fetchFeedbackList,
  );
  const updateFetchParams = useFeedbackListStore(
    (state) => state.updateFetchParams,
  );

  const fetchList = async (page?: number) => {
    let start: string;
    let end: string;
    if (typeof props.selectedChartBar?.dataIndex === 'number') {
      const label = props.trendChartData.labels?.[
        props.selectedChartBar.dataIndex
      ] as string;

      const parsed = parseDateFormatByRangeUnit(
        label,
        props.dateState.rangeUnit,
      );
      start = parsed.start;
      end = parsed.end;
    } else {
      start =
        props.dateState.dateRange?.[0].toJSON() ||
        getTimezoneDayjs().subtractVal(3, 'month').toJSON();
      end =
        props.dateState.dateRange?.[1].toJSON() || getTimezoneDayjs().toJSON();
    }
    updateFetchParams({
      sortStatus: props.sortStatus,
      feedbackListFilter: props.filterQueryFormats,
      page: page || 0,
      dateRange: [start, end],
    });
    await fetchFeedbackList();
    props.clearCheckList();
    if (props.searchValue) {
      highlight(props.searchValue, '.feedback-list-title-span');
    } else {
      removeHighlight('.feedback-list-title-span');
    }
  };

  useDeepCompareEffect(() => {
    fetchList();
  }, [
    props.searchValue,
    props.dateState,
    props.filterQueryFormats,
    props.sortStatus,
    props.selectedChartBar?.datasetIndex,
    props.trendChartData.labels,
  ]);

  return fetchList;
};

export default useFetchFeedbackList;
